import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { initials } from "@/lib/statuts";
import {
  LayoutDashboard, Navigation, CalendarDays, Map, Route as RouteIcon,
  ListChecks, Users2, Building2, UserCog, BarChart3, Wallet, LogOut, Menu, X, Search, MapPin, SlidersHorizontal, Coins, Sun, FolderKanban,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const NAV_GROUPS = [
  { title: "Planning", items: [
    { to: "/", label: "Tableau de bord", icon: LayoutDashboard, roles: ["admin", "planification", "technicien", "installateur"], end: true },
    { to: "/plus-proche", label: "Plus proche", icon: Navigation, roles: ["admin", "planification"] },
    { to: "/planification", label: "Planification", icon: CalendarDays, roles: ["admin", "planification", "technicien", "installateur"], labelByRole: { technicien: "Mon planning" } },
    { to: "/carte", label: "Carte", icon: Map, roles: ["admin", "planification"] },
    { to: "/tournee", label: "Tournée", icon: RouteIcon, roles: ["admin", "planification"] },
    { to: "/ma-tournee", label: "Ma tournée", icon: RouteIcon, roles: ["technicien"] },
  ] },
  { title: "Données", items: [
    { to: "/rendez-vous", label: "Rendez-vous", icon: ListChecks, roles: ["admin", "planification", "technicien", "installateur"], labelByRole: { technicien: "Mes rendez-vous", installateur: "Mes dossiers" } },
    { to: "/clients", label: "Clients", icon: Users2, roles: ["admin", "planification", "installateur"] },
    { to: "/partenaires", label: "Partenaires", icon: Building2, roles: ["admin", "planification"] },
    { to: "/utilisateurs", label: "Utilisateurs", icon: UserCog, roles: ["admin", "planification"] },
    { to: "/statistiques", label: "Statistiques", icon: BarChart3, roles: ["admin", "planification"] },
    { to: "/comptabilite", label: "Comptabilité", icon: Wallet, roles: ["admin"] },
    { to: "/mes-jetons", label: "Mes jetons", icon: Coins, roles: ["installateur"] },
    { to: "/parametrage", label: "Paramétrage", icon: SlidersHorizontal, roles: ["admin"] },
  ] },
  { title: "NovaSol", items: [
    { to: "/novasol", label: "Dashboard NovaSol", icon: Sun, roles: ["admin", "planification"], end: true },
    { to: "/novasol/rendez-vous", label: "Rendez-vous NovaSol", icon: CalendarDays, roles: ["admin", "planification"] },
    { to: "/novasol/dossiers", label: "Dossiers NovaSol", icon: FolderKanban, roles: ["admin", "planification"] },
  ] },
];

function SidebarContent({ user, onNavigate }) {
  const role = user.role;
  const groups = NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => i.roles.includes(role)) })).filter((g) => g.items.length);
  return (
    <div className="flex h-full flex-col bg-[#0A0A0A]">
      <div className="flex items-center gap-2 px-5 py-5 border-b border-white/10">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600">
          <MapPin className="h-5 w-5 text-white" />
        </div>
        <span className="font-heading text-2xl font-black tracking-tighter text-white">CVT</span>
      </div>
      <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
        {groups.map((g) => (
          <div key={g.title} className="space-y-1">
            <div className="px-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-slate-500">{g.title}</div>
            {g.items.map((item) => {
              const label = item.labelByRole?.[role] || item.label;
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  data-testid={`nav-${item.to === "/" ? "dashboard" : item.to.slice(1)}`}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
                      isActive ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5 hover:text-slate-200"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon className={`h-[18px] w-[18px] ${isActive ? "text-white" : "text-slate-500 group-hover:text-slate-300"}`} />
                      {label}
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>
      <div className="border-t border-white/10 px-4 py-3 text-[11px] text-slate-500">CRM CVT · Visites techniques</div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState("");

  const roleColors = { admin: "bg-rose-600", planification: "bg-blue-600", technicien: "bg-emerald-600", installateur: "bg-amber-600" };

  const doSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/rendez-vous?q=${encodeURIComponent(search.trim())}`);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-[#F8FAFC]">
      <aside className="hidden w-64 flex-shrink-0 lg:block">
        <SidebarContent user={user} />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-64">
            <SidebarContent user={user} onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-slate-200 bg-white px-4 sm:px-6">
          <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="h-6 w-6 text-slate-700" />
          </button>
          <form onSubmit={doSearch} className="relative hidden max-w-md flex-1 sm:block">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="global-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Recherche de ticket, client, code postal…"
              className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm outline-none transition-colors duration-200 focus:border-slate-400 focus:bg-white"
            />
          </form>
          <div className="ml-auto flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-2">
                  <div className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold text-white ${roleColors[user.role] || "bg-slate-600"}`}>
                    {initials(user.prenom, user.nom)}
                  </div>
                  <div className="hidden text-left sm:block">
                    <div className="text-sm font-semibold leading-tight text-slate-900">{user.prenom} {user.nom}</div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-400">{user.role_label}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold">{user.prenom} {user.nom}</div>
                  <div className="text-xs font-normal text-slate-500">{user.email}</div>
                  {user.entreprise && <div className="text-xs font-normal text-slate-500">{user.entreprise.raison_sociale}</div>}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/mot-de-passe")} data-testid="menu-password">Changer mot de passe</DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="nav-logout" className="text-rose-600">
                  <LogOut className="mr-2 h-4 w-4" /> Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 sm:p-6" key={location.pathname}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
